import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XMarkIcon, PlusCircleIcon, CheckIcon } from '@heroicons/react/24/outline';
import { userAPI as profileAPI } from '../../services/api';
import { Portfolio } from '../../types';
import Button from '../common/Button';
import toast from 'react-hot-toast';
import useLockBodyScroll from '../../hooks/useLockBodyScroll';
import SmartImage from '../common/SmartImage';
import { resolveImageUrl } from '../../utils/image';

interface AddItemsToFolderModalProps {
  onClose: () => void;
  onSuccess: () => void;
  targetUserId: string;
  folderId: string;
  folderName: string;
}

const AddItemsToFolderModal: React.FC<AddItemsToFolderModalProps> = ({ 
  onClose, 
  onSuccess, 
  targetUserId,
  folderId,
  folderName
}) => {
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<Portfolio[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [fetchingItems, setFetchingItems] = useState(false);
  
  const observer = React.useRef<IntersectionObserver | null>(null);

  useLockBodyScroll();

  // Fetch items logic
  useEffect(() => {
    const fetchItems = async () => {
      if (fetchingItems || !hasMore) return;
      
      setFetchingItems(true);
      try {
        // Fetch items - backend filtrering for items already in folder is ideal, 
        // but we'll fetch all and backend handler handles duplicates elegantly
        const response = await profileAPI.getUserPortfolio(targetUserId, page, 24);
        if (response.data.success) {
          const newItems = response.data.data.portfolio || [];
          setItems(prev => page === 1 ? newItems : [...prev, ...newItems]);
          setHasMore(response.data.data.has_more || false);
        }
      } catch (error) {
        console.error('Failed to fetch portfolio items for folder selection:', error);
        setHasMore(false);
      } finally {
        setFetchingItems(false);
      }
    };

    fetchItems();
  }, [targetUserId, page]);

  // Fetch existing folder items for pre-selection
  useEffect(() => {
    const fetchExistingItems = async () => {
      if (!folderId || !targetUserId) return;
      
      try {
        const response = await profileAPI.getFolderItems(targetUserId, folderId, 1, 100);
        if (response.data.success) {
          const folderItems = response.data.data.portfolio || [];
          const folderItemIds = folderItems.map((item: any) => item.id);
          setSelectedIds(prev => Array.from(new Set([...prev, ...folderItemIds])));
        }
      } catch (error) {
        console.error('Failed to fetch existing folder items for pre-selection:', error);
      }
    };

    fetchExistingItems();
  }, [folderId, targetUserId]);

  // Observer for infinite scroll
  useEffect(() => {
    const lastElementId = `item-${items.length - 1}`;
    const lastElement = document.getElementById(lastElementId);
    if (!lastElement || !hasMore) return;

    if (observer.current) observer.current.disconnect();

    observer.current = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && hasMore && !fetchingItems) {
        setPage(prev => prev + 1);
      }
    }, { threshold: 0.1, rootMargin: '200px' });

    observer.current.observe(lastElement);

    return () => observer.current?.disconnect();
  }, [items.length, hasMore, fetchingItems]);

  const toggleSelection = (id: string) => {
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const handleSubmit = async () => {
    if (selectedIds.length === 0) {
      toast.error('Please select at least one item');
      return;
    }

    setLoading(true);
    try {
      await profileAPI.addItemsToFolder(folderId, selectedIds);
      toast.success('Items added to folder');
      onSuccess();
    } catch (error) {
      toast.error('Failed to add items to folder');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[80] flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-white dark:bg-gray-900 rounded-2xl max-w-2xl w-full max-h-[90vh] flex flex-col shadow-2xl overflow-hidden border border-gray-100 dark:border-gray-800"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 md:p-6 border-b border-gray-200 dark:border-gray-800 shrink-0">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary-50 dark:bg-primary-900/20 rounded-xl">
                <PlusCircleIcon className="h-5 w-5 text-primary-600 dark:text-primary-400" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Add to {folderName}
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Select existing items from your portfolio
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
              disabled={loading}
            >
              <XMarkIcon className="h-5 w-5 text-gray-400" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-6">
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
              {items.map((item, index) => (
                <button
                  key={item.id}
                  id={`item-${index}`}
                  type="button"
                  onClick={() => toggleSelection(item.id)}
                  className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-all ${
                    selectedIds.includes(item.id)
                      ? 'border-primary-500 ring-2 ring-primary-500/30'
                      : 'border-transparent hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                >
                  <SmartImage
                    src={resolveImageUrl(item.thumbnail_image_url_signed || item.thumbnail_image_url)}
                    alt={item.title}
                    className={`w-full h-full object-cover transition-all duration-300 ${
                      selectedIds.includes(item.id) ? 'blur-[2px] scale-110' : ''
                    }`}
                  />
                  {selectedIds.includes(item.id) && (
                    <div className="absolute inset-0 bg-black/10 flex items-center justify-center">
                      <motion.div 
                        initial={{ scale: 0, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        className="bg-gradient-to-br from-orange-400 to-orange-600 rounded-full p-1.5 shadow-lg shadow-orange-500/40 ring-2 ring-white"
                      >
                        <CheckIcon className="h-4 w-4 text-white stroke-[3]" />
                      </motion.div>
                    </div>
                  )}
                </button>
              ))}
              
              {(fetchingItems || loading) && (
                <div className="col-span-full flex justify-center py-4">
                  <div className="h-8 w-8 border-2 border-primary-500 border-t-transparent animate-spin rounded-full" />
                </div>
              )}
              
              {!fetchingItems && items.length === 0 && !loading && (
                <div className="col-span-full py-12 text-center">
                  <p className="text-gray-500 dark:text-gray-400">No portfolio items available to add</p>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="p-4 md:p-6 border-t border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900 shrink-0 flex space-x-3">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="primary"
              onClick={handleSubmit}
              loading={loading}
              disabled={selectedIds.length === 0 || loading}
              className="flex-1"
            >
              Add {selectedIds.length > 0 ? `(${selectedIds.length})` : ''} Items
            </Button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default AddItemsToFolderModal;
